// This file should be considered SoT for internal route paths
import { ReactElement, useMemo } from 'react';

import { matchPath, useLocation } from '@remix-run/react';

import { BUYER_URL, DOMAIN } from 'shared/helpers/globals';

// typescript
export type RouteComponentOutput = ReactElement;

// Query Params
export const addressUUIDParam = ':addressUUID';
export const addressTokenParam = ':addressToken';
export const offerIdParam = ':offerId'; // BwOD offer ID

export const ACCOUNT_URL = `${DOMAIN}/account`;
export const LOGOUT_URL = `${DOMAIN}/logout`;
export const DIRECT_MAIL_PREFERENCES_URL = `${DOMAIN}/mail`;
export const TERMS_OF_SERVICE_URL = `${DOMAIN}/terms`;
export const PRIVACY_POLICY_URL = `${DOMAIN}/privacy`;
export const FAQ_URL = 'https://help.opendoor.com/hc/en-us';
export const PRIVACY_CENTER_URL = 'https://privacy.opendoor.com';
export const OPENDOOR_BROKERAGE_URL = `${TERMS_OF_SERVICE_URL}/#brokerage_representation`;
export const BROKERAGE_SERVICES_URL =
  'https://www.opendoor.com/w/wp-content/uploads/2020/12/opendoor-brokerage-TREC-IABS-1-0.pdf';
export const CCPA_URL = 'https://www.trec.texas.gov/forms/consumer-protection-notice';

export const CLOSING_GUIDE_URL = `${BUYER_URL}/closing/win`;
export const BUY_GUIDE_URL = `${BUYER_URL}/offers/win`;

/** Returns the URL for the buyer's email frequency page for a given humanToken */
export const getBuyerEmailFrequencyURL = (humanToken: string) =>
  `${BUYER_URL}/users/${humanToken}/email-frequency/edit`;

/** @deprecated - The legacy base routes for all of cake */
export const CUSTOMER_ADDRESS_BASE_ROUTE = `/dashboard/customer/`;
/** @deprecated */
export const MONITOR_BASE_ROUTE = `/dashboard/monitor/`;
/** @deprecated */
export const DASHBOARD_BASE_ROUTE = '/dashboard/';
/** @deprecated */
export const ADDRESS_OVERVIEW_BASE_ROUTE = `/dashboard/overview`;

// The base routes for all of cake
export const ADDRESS_SELL_BASE_ROUTE = `/dashboard/sell`;
export const ADDRESS_HOW_IT_WORKS_BASE_ROUTE = `/dashboard/how-it-works`;
export const ADDRESS_MORE_ROUTE = `/dashboard/more`;
export const ADDRESS_CONTACT_BASE_ROUTE = `/dashboard/contact`;
export const ADDRESS_BUY_SELL_PROGRESS_BASE_ROUTE = '/dashboard/buy-sell-progress';

export const CONTRACT_SIGNATURE_ROUTE = `/dashboard/sign`;

export const BUY_ROUTE = '/dashboard/buy';
export const BUYER_OFFER_BASE_ROUTE = `${BUY_ROUTE}/offer`;
export const BUYER_BUY_DIRECT_CHECKOUT_FLOW_ROUTE = `${BUY_ROUTE}/checkout`;
export const BUYER_OFFER_PRICE_GUIDANCE_BASE_ROUTE = `${BUY_ROUTE}/price-guidance`;
export const BUYER_SHOWING_REQUEST_CREATION_ROUTE = `${BUY_ROUTE}/tour`;
/** @deprecated */
export const CLOSING_DASHBOARD_BASE_ROUTE = '/dashboard/closing';

export const BUYER_PRODUCT_ROUTES = [
  BUYER_OFFER_BASE_ROUTE,
  BUYER_BUY_DIRECT_CHECKOUT_FLOW_ROUTE,
  BUYER_OFFER_PRICE_GUIDANCE_BASE_ROUTE,
  BUYER_SHOWING_REQUEST_CREATION_ROUTE,
  CONTRACT_SIGNATURE_ROUTE,
];

export const BUY_ONLY_SUB_PATHS = [
  `${BUY_ROUTE}/feed`,
  `${BUY_ROUTE}/search`,
  `${BUY_ROUTE}/my-offers`,
  `${BUY_ROUTE}/favorites`,
];

export const HOW_IT_WORKS_SUB_PATHS = [
  `${ADDRESS_HOW_IT_WORKS_BASE_ROUTE}/sell`,
  `${ADDRESS_HOW_IT_WORKS_BASE_ROUTE}/sell-and-buy`,
];

export const CONTACT_SUB_PATHS = [
  `${ADDRESS_CONTACT_BASE_ROUTE}/support`,
  `${ADDRESS_CONTACT_BASE_ROUTE}/title`,
  `${ADDRESS_CONTACT_BASE_ROUTE}/sell`,
  `${ADDRESS_CONTACT_BASE_ROUTE}/buy`,
];

export const HUB_BASE_ROUTES = [
  ADDRESS_SELL_BASE_ROUTE,
  BUY_ROUTE,
  ...BUY_ONLY_SUB_PATHS,
  ...HOW_IT_WORKS_SUB_PATHS,
  ADDRESS_BUY_SELL_PROGRESS_BASE_ROUTE,
  ADDRESS_HOW_IT_WORKS_BASE_ROUTE,
  ADDRESS_MORE_ROUTE,
  ADDRESS_CONTACT_BASE_ROUTE,
  ...CONTACT_SUB_PATHS,
];

export const HUB_ROUTES = [
  ADDRESS_SELL_BASE_ROUTE + `/${addressUUIDParam}?`,
  BUY_ROUTE + `/${addressUUIDParam}?`,
  ADDRESS_BUY_SELL_PROGRESS_BASE_ROUTE + `/${addressUUIDParam}`,
  ADDRESS_HOW_IT_WORKS_BASE_ROUTE + `/${addressUUIDParam}?`,
  ADDRESS_MORE_ROUTE + `/${addressUUIDParam}?`,
  ADDRESS_CONTACT_BASE_ROUTE + `/${addressUUIDParam}?`,
];

// All the following routes are ONLY used only for navigation purposes NOT route definition

/** @deprecated */
export const getCustomerAddressBaseRoute = (customerUUID: string, addressUUID: string) =>
  `${CUSTOMER_ADDRESS_BASE_ROUTE}${customerUUID}/${addressUUID}`;

/** @deprecated */
export const getLegacyDashboardBaseRoute = (sellerInputUUID: string) =>
  `${DASHBOARD_BASE_ROUTE}${sellerInputUUID}`;

/** Returns whether or not the route uses the new hub routing structure */
export const isRouteHubDashboardPath = (pathname: string) =>
  // Matches the base routes exactly
  // routes w/ optional addressUUIDs can contain subpaths
  [...HUB_ROUTES, ...HUB_BASE_ROUTES].some(
    (path) => !!matchPath({ path, end: true }, pathname) || pathname.startsWith(path),
  );

/** Determines whether or not the provided subRoute is active */
export const isSubRouteActive = (pathname: string, subRoutePath: string) =>
  !!matchPath({ path: subRoutePath, end: true }, pathname);

/**  Determines whether or not the provided subRoute is active */
export const useActiveSubRoute = () => {
  const location = useLocation();
  return useMemo(
    () => ({
      isSubRouteActive: (subRoutePath: string) => isSubRouteActive(location.pathname, subRoutePath),
    }),
    [location.pathname],
  );
};

/** Parses the base route from the provided url path  */
export const parseBaseRoute = (pathname: string, isBuyOnlyCustomer?: boolean) => {
  // Remove the empty string at splitPath[0] since the first char in the path is /
  const splitPath = pathname.split('/').slice(1);
  const isHubDashboardPath = isRouteHubDashboardPath(pathname);

  if (isHubDashboardPath) {
    //  Base route
    // /dashboard/{base_path}/{sub_path}
    // /----0----/-----1-----/----2-----
    const isHubBaseRoute = HUB_BASE_ROUTES.some(
      (path) => !!matchPath({ path, end: true }, pathname),
    );

    //  Route with addressUUID
    // /dashboard/{base_path}/:addressUUID?/{sub_path}
    // /----0----/-----1-----/------2------/----3-----
    // ? indicates that the addressUUID is an optional param
    const base = `/${splitPath[0]}/${splitPath[1]}`;
    return isHubBaseRoute && isBuyOnlyCustomer ? base : `${base}/${addressUUIDParam}?`;
  }

  // no valid base path
  return '/error';
};

/** Parses the base route from the current URL */
export const getBaseRoute = () => {
  const path = window.location.pathname;
  return parseBaseRoute(path);
};

// Functions to generate the sub-routes within cake

/** @deprecated MTM home-details route */
export const getHomeDetailsRoute = () => `${getHubOverviewRoute()}/home-details`;

/**
 * HUB ROUTES
 */

/** @deprecated Overview route -- Route format which includes an optional addressUUID */
export const getHubOverviewRoute = () => `${ADDRESS_OVERVIEW_BASE_ROUTE}/${addressUUIDParam}?`;

export const getHubMoreRoute = () => `${ADDRESS_MORE_ROUTE}/${addressUUIDParam}?`;

/** Contact route*/
export const getHubContactRoute = () => `${ADDRESS_CONTACT_BASE_ROUTE}/${addressUUIDParam}?`;
export const getHubContactSupportRoute = () => `${getHubContactRoute()}/support`;
export const getHubContactSellRoute = () => `${getHubContactRoute()}/sell`;
export const getHubContactsTitleRoute = () => `${getHubContactRoute()}/title`;
export const getHubContactsBuyRoute = () => `${getHubContactRoute()}/buy`;

// Buying routes -- Route format which includes an optional addressUUID
export const getHubBuyingRoute = () => `${BUY_ROUTE}/${addressUUIDParam}?`;
export const getHubBuyingSearchRoute = () => `${getHubBuyingRoute()}/search`;
export const getHubBuyingFeedRoute = () => `${getHubBuyingRoute()}/feed`;
export const getHubBuyingFeedFiltersRoute = () => `${getHubBuyingFeedRoute()}?edit=1`;
export const getHubBuyingFavoritesRoute = () => `${getHubBuyingRoute()}/favorites`;
export const getHubBuyingMyOffersRoute = () => `${getHubBuyingRoute()}/my-offers`;

// Selling routes
/** Base selling route -- Route format which includes an optional addressUUID */
export const getHubSellingRoute = () => ADDRESS_SELL_BASE_ROUTE + `/${addressUUIDParam}?`;
/** Entrypoint to a multi-step referral form */
export const getAgentReferralFormRoute = () => `${getHubSellingRoute()}/agent-referral`;
export const getHubCompsRoute = () => `${getHubSellingRoute()}/comps`;
export const getHubORVAScheduleRoute = () => `${getHubSellingRoute()}/schedule`;
export const getHubScheduleOfferReviewRoute = () => `${getHubSellingRoute()}/schedule`;
/**
 * Route for SSVA targeting experiment. Asks user about the home occupancy and directs them to SSVA/ORVA
 */
export const getHubVirtualAssessmentQuestionsRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment-questions`;
export const getHubSSVAUploadRoute = () => `${getHubSellingRoute()}/virtual-assessment/upload`;
export const getHubSSVAUploadWholeHomeRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment/upload/whole-home`;
export const getHubSSVAUploadSellerDisclosureDocumentRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment/upload/seller-disclosure`;
export const getHubSSVAUploadInteriorRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment/upload/interior`;
export const getHubSSVAUploadBackyardRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment/upload/backyard`;
export const getHubSSVAUploadFrontyardRoute = () =>
  `${getHubSellingRoute()}/virtual-assessment/upload/frontyard`;
/** Page for requesting a late checkout */
export const getHubLateCheckoutWizardRoute = () => `${getHubSellingRoute()}/late-checkout`;
export const getHubHomeDetailsRoute = () => `${getHubSellingRoute()}/home-details`;
export const getCompleteFormsRoute = () => `${getHubSellingRoute()}/complete-forms`;
export const getExteriorAssessmentSelfSchedulingRoute = () =>
  getHubSellingRoute() + `/exterior-assessment/schedule`;
/** route to cancel home assessment */
export const getExteriorAssessmentCancelRoute = () =>
  getHubSellingRoute() + `/exterior-assessment/cancel`;
/** route to schedule or cancel a home assessment */
export const getExteriorAssesssmentScheduleRoute = () =>
  getHubSellingRoute() + `/exterior-assessment/keep-schedule`;

// How it works routes
export const getHubHowItWorksRoute = () =>
  `${ADDRESS_HOW_IT_WORKS_BASE_ROUTE}/${addressUUIDParam}?`;
export const getLearnAboutPricingRoute = () => `${FAQ_URL}/articles/21797406040717`;
export const getNewHubHowItWorksSellRoute = () => `${FAQ_URL}/articles/7965862785933`;

/** Buyer Closing Dashboard Routes */
export const getBuyerClosingBaseRoute = (offerId: string) =>
  `${CLOSING_DASHBOARD_BASE_ROUTE}/${offerId}/home`;

/** Buyer offer route */
export const getBuyerOfferRoute = (offerId: string) => `${BUYER_OFFER_BASE_ROUTE}/${offerId}`;

/** Price guidance route */
export const getBuyerOfferPriceGuidanceRoute = (addressToken: string) =>
  `${BUYER_OFFER_PRICE_GUIDANCE_BASE_ROUTE}/${addressToken}`;
/** Price guidance route */
export const getBuyerOfferPriceGuidanceById = (addressToken: string, offerId: string) =>
  `${BUYER_OFFER_PRICE_GUIDANCE_BASE_ROUTE}/${addressToken}?offerId=${offerId}`;

/** Public price guidance route */
export const getPublicPriceGuidanceRoute = (addressToken: string) =>
  `/dashboard/price-guidance/${addressToken}`;
